import React from 'react';
import { stub as $t } from '@nbcnews/analytics-framework';
import { isLengthGreaterThan8 } from 'lib/fontBasedOnTextLength';
import classNames from 'classnames';
import { isOnSale } from 'lib/productOfferPriceHelpers';
import { useTranslation } from 'react-i18next';

/**
 *
 * @param {object} props
 * @param {string} props.listPrice - list price in the form '#.##'
 * @param {string} props.salePrice - only present if item is on sale, also arrives as '#.##'
 * @param {string} props.seller - name of the retailer selling the product in question
 * @returns {React.ReactElement}
 */
function ShopButtonContent({
  listPrice = '',
  salePrice = '',
  seller = '',
}) {
  let priceText;
  const renderSale = isOnSale({ salePrice, listPrice });
  const { t } = useTranslation();

  if (renderSale) {
    priceText = (
      <>
        <span
          className="salePrice"
          data-testid="shop-button__sale-price"
        >
          {`$${salePrice} `}
        </span>
        <span
          className="listPrice onSale"
          data-testid="shop-button__strikeout-list-price"
        >
          {`$${listPrice}`}
        </span>
      </>
    );
  } else {
    priceText = (
      <span
        className="listPrice"
        data-testid="shop-button__list-price"
      >
        {`$${listPrice}`}
      </span>
    );
  }

  return (
    <span
      className="shop-button-info__vendor-name"
      data-testid="shop-button-variation"
    >
      {priceText}
      {seller && (
        <span
          className="sellerText"
          data-testid="shop-button-variation__seller-text"
        >
          {` ${t('at')} ${seller}`}
        </span>
      )}
    </span>
  );
}

/**
 * Renders a commerce button
 * @param {object} props
 * @param {object} props.additionalStyles
 * @param {string} props.block
 * @param {bool} props.isWaffle
 * @param {boolean} props.isBacon
 * @param {string} props.listPrice
 * @param {string} props.salePrice
 * @param {string} props.name
 * @param {string} props.seller
 * @param {boolean} props.shouldTrackClicks
 * @param {string} props.trackingCode
 */
function ShopButton({
  additionalStyles,
  block,
  isWaffle,
  isBacon = false,
  listPrice,
  salePrice,
  name,
  seller,
  shouldTrackClicks = true,
  trackingCode = 'mbt_ecommerce_shop_button',
}) {
  if (!listPrice && !salePrice) return null;

  const bemElemVendorBtn = `${block}__vendorBtn`;
  const isLongerThan8Chars = isLengthGreaterThan8(seller);
  $t('register', trackingCode, { allowDuplicate: true });

  return (
    <button
      tabIndex={-1}
      type="button"
      data-testid="shop-button-info"
      className={classNames(
        bemElemVendorBtn,
        additionalStyles?.vendor,
        'shop-button-info__vendor',
        {
          'shop-button-info__vendor-fit-text pl3-m':
              isLongerThan8Chars && isWaffle,
          'shop-button-info__vendor-fit-text': isLongerThan8Chars,
          'shop-button-info__bacon-button': isBacon,
          'shop-button-info__not-bacon-button': !isBacon,
        },
      )}
      onClick={shouldTrackClicks ? (() => {
        $t('track', trackingCode, {
          action: 'itemClick',
          name,
          listPrice,
          seller,
        });
      }) : undefined}
    >
      <ShopButtonContent listPrice={listPrice} salePrice={salePrice} seller={seller} />
    </button>
  );
}

export default ShopButton;
